// Roster management helpers: US states, residency derivation, position groups.

export const STATE_NAME_TO_ABBR = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO",
  montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND", ohio: "OH",
  oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
  "district of columbia": "DC",
};

export const US_STATE_ABBRS = new Set(Object.values(STATE_NAME_TO_ABBR));

export const RESIDENCY_META = {
  in_state: { label: "In-State", color: "#22c55e" },
  regional: { label: "Regional", color: "#3b82f6" },
  out_state: { label: "Out-of-State", color: "#f97316" },
  unknown: { label: "Unknown", color: "#6b7280" },
};

export const RESIDENCY_OPTIONS = [
  { value: "", label: "Auto" },
  { value: "in_state", label: "In-State" },
  { value: "regional", label: "Regional" },
  { value: "out_state", label: "Out-of-State" },
];

// Canonical football position sort order (depth-chart reading order). SINGLE SOURCE OF TRUTH —
// used app-wide for roster lists, team worksheets, and PDF/print/email exports.
// Positions outside this list sort after, alphabetically.
export const FB_POS_ORDER = ["QB", "RB", "FB", "TE", "WR", "OC", "OG", "OT", "NT", "DT", "DE", "EDGE", "ILB", "OLB", "CB", "NB", "S", "P", "K", "LS"];

// Granular / alias position codes normalized to their canonical base so variant labels
// (MLB, FS, LG, LDE, X/Z, …) sort in the right spot instead of sinking to the bottom.
export const FB_POS_ALIAS = {
  LT: "OT", RT: "OT", LG: "OG", RG: "OG", C: "OC", OL: "OC", G: "OG", T: "OT",
  HB: "RB", TB: "RB", SLOT: "WR", X: "WR", Z: "WR", Y: "TE", SE: "WR", FL: "WR", HBACK: "TE",
  LDE: "DE", RDE: "DE", JACK: "DE", DL: "DT",
  MLB: "ILB", WLB: "OLB", SLB: "OLB", LB: "ILB",
  LCB: "CB", RCB: "CB", DB: "S", FS: "S", SS: "S",
  PK: "K", PT: "P", LSN: "LS",
};

// Rank a position by canonical order (alias-normalized). Unknown positions return
// FB_POS_ORDER.length (sink to bottom).
export const fbPosRank = (pos) => {
  const p = (pos || "").toUpperCase().trim();
  const base = FB_POS_ALIAS[p] || p;
  const idx = FB_POS_ORDER.indexOf(base);
  return idx === -1 ? FB_POS_ORDER.length : idx;
};

// Canonical base position (alias-normalized), and OL detection.
export const fbBasePos = (pos) => { const p = (pos || "").toUpperCase().trim(); return FB_POS_ALIAS[p] || p; };
export const isOLPos = (pos) => ["OC", "OG", "OT"].includes(fbBasePos(pos));
// Order within the OL for a printout: LT → LG → C → RG → RT.
export const OL_SPOTS = ["LT", "LG", "C", "RG", "RT"];
export const olSpotRank = (spot) => { const i = OL_SPOTS.indexOf((spot || "").toUpperCase().trim()); return i === -1 ? OL_SPOTS.length : i; };

// Compare two positions by canonical football order, falling back to alphabetical for unknowns.
export function compareFootballPos(a, b) {
  const ra = fbPosRank(a), rb = fbPosRank(b);
  if (ra !== rb) return ra - rb;
  const pa = (a || "").toUpperCase().trim(), pb = (b || "").toUpperCase().trim();
  return pa < pb ? -1 : pa > pb ? 1 : 0;
}

// Football position groups (mirrors the depth chart layout).
export const POS_GROUPS = [
  ["Offense", ["QB", "RB", "FB", "TE", "WR", "OC", "OG", "OT"]],
  ["Defense", ["NT", "DT", "DE", "ILB", "OLB", "CB", "S"]],
  ["Special Teams", ["P", "K", "LS"]],
];

// Roles allowed to toggle a player's Transfer-Risk (flight-risk) flag.
// NOTE: "operations" stands in for "Chief of Staff" (no dedicated role exists yet).
export const TRANSFER_RISK_ROLES = ["super_admin", "head_coach", "gm", "operations"];

// Starter slots per position (mirrors the depth chart) — used for the quality-NEED flag.
export const STARTER_SLOTS = {
  QB: 1, RB: 1, FB: 1, TE: 1, WR: 3, OC: 1, OG: 2, OT: 2,
  NT: 1, DT: 1, DE: 3, ILB: 2, OLB: 2, CB: 2, S: 2, K: 1, P: 1, LS: 1,
};

// Roster tiers (1 = best). Labels are configurable per school; these are the defaults.
export const DEFAULT_TIER_LABELS = [
  "Impact / Difference Maker",
  "Starter",
  "Role Starter",
  "High Contributor / Push to Start",
  "High Backup / Special Teams Contributor",
  "Backup / Practice Player",
  "Camp Player / Walk-On / Limited Upside",
  "Not For the Tigers",
];
export const TIER_COLORS = { 1: "#16a34a", 2: "#22c55e", 3: "#84cc16", 4: "#eab308", 5: "#f59e0b", 6: "#f97316", 7: "#ef4444", 8: "#6b7280" };
export const tierColor = (t) => TIER_COLORS[t] || "#3f3f46";

// Scholarship level derived from projected tier: T1-T2 = Full, T3-T4 = Half, T5-T6 = Partial, T7-T8 = Walk-On.
export const SCHOL_LEVEL_META = {
  full: { label: "Full", color: "#16a34a" },
  half: { label: "Half", color: "#eab308" },
  partial: { label: "Partial", color: "#f97316" },
  walk_on: { label: "Walk-On", color: "#6b7280" },
};
// Ordered scholarship buckets for the Decision Board scholarship-stack view.
export const SCHOL_BUCKETS = [
  { key: "full", label: "Full", color: "#16a34a" },
  { key: "half", label: "Half", color: "#eab308" },
  { key: "partial", label: "Partial", color: "#f97316" },
  { key: "walk_on", label: "Walk-On", color: "#6b7280" },
];
// Program-set "Partial" offer equivalency fraction (default 25%).
export const partialFrac = (config) => (Number(config?.partial_schol_pct) || 25) / 100;
// Equivalency fraction for a scholarship level (partial pulls from config).
export function scholFracForLevel(level, config) {
  if (level === "full") return 1;
  if (level === "half") return 0.5;
  if (level === "partial") return partialFrac(config);
  return 0; // walk_on / unset
}
// Bucket a roster player into a scholarship level from their actual aid %.
export function scholBucketForPct(pct) {
  const p = Number(pct) || 0;
  if (p >= 100) return "full";
  if (p >= 50) return "half";
  if (p > 0) return "partial";
  return "walk_on";
}
export function scholLevelForTier(tier) {
  const t = Number(tier);
  if (t === 1 || t === 2) return "full";
  if (t === 3 || t === 4) return "half";
  if (t === 5 || t === 6) return "partial";
  if (t >= 7) return "walk_on";
  return null;
}
// Effective scholarship level for a recruit: explicit override wins, else derived from tier.
export function recruitScholLevel(r) {
  if (r && r.schol_level && SCHOL_LEVEL_META[r.schol_level]) return r.schol_level;
  return scholLevelForTier(r && r.tier);
}

// Equivalency fraction (0..1) a recruit's offer represents: an exact offer_pct wins when set,
// otherwise fall back to their Full/Half/Partial/Walk-On level. Single source of truth so the
// Decision Board's $ / spot reconciliation always matches whatever the coach entered.
export function recruitEquivFrac(r, config) {
  const pct = r == null ? null : r.offer_pct;
  if (pct !== null && pct !== undefined && pct !== "" && !Number.isNaN(Number(pct))) {
    return Math.max(0, Math.min(100, Number(pct))) / 100;
  }
  return scholFracForLevel(recruitScholLevel(r) || "walk_on", config);
}

// Parse a 2-letter state abbreviation from a free-text hometown ("Atlanta, GA").
export function parseStateAbbr(hometown) {
  if (!hometown) return null;
  const s = String(hometown).trim();
  const m = s.match(/([A-Za-z]{2})\.?\s*$/);
  if (m) {
    const abbr = m[1].toUpperCase();
    if (US_STATE_ABBRS.has(abbr)) return abbr;
  }
  const lower = s.toLowerCase();
  for (const [name, abbr] of Object.entries(STATE_NAME_TO_ABBR)) {
    if (lower.includes(name)) return abbr;
  }
  return null;
}

// Returns in_state | regional | out_state | unknown
export function deriveResidency(hometown, config) {
  if (!config) return "unknown";
  const abbr = parseStateAbbr(hometown);
  if (!abbr) return "unknown";
  if (abbr === (config.home_state || "").toUpperCase()) return "in_state";
  if ((config.regional_states || []).map((x) => x.toUpperCase()).includes(abbr)) return "regional";
  return "out_state";
}

// ---- shared cost-of-attendance / player-cost helpers (single source of truth) ----
// Cost of attendance for a residency tier (unknown bills at out-of-state, matching the ledger).
export function coaOf(config, residency) {
  if (!config) return 0;
  const r = residency === "unknown" ? "out_state" : residency;
  const t = r === "in_state" ? config.tuition_in_state : r === "regional" ? config.tuition_regional : config.tuition_out_state;
  return (Number(t) || 0) + (Number(config.fees_room_board) || 0);
}

export function effResidencyOf(player, config) {
  return player.residency || deriveResidency(player.hometown, config);
}

export function effPctOf(player) {
  const v = player.scholarship_pct;
  return (v == null || v === "") ? (player.on_scholarship ? 100 : 0) : Number(v);
}

export function playerCostOf(player, config) {
  if (player.cost_override != null && player.cost_override !== "") return Number(player.cost_override);
  return coaOf(config, effResidencyOf(player, config)) * effPctOf(player) / 100;
}

// ---- Planned next-season aid (Retention Board "Increase / Reduce aid" decisions) ----
// Single source of truth for every next-season money view; live aid stays untouched until applied.
export const AID_PLAN_DECISIONS = ["increase", "reduce"];
export function plannedPctOf(p) {
  if (!AID_PLAN_DECISIONS.includes(p?.roster_decision)) return null;
  const v = p.planned_aid_pct;
  if (v == null || v === "" || Number.isNaN(Number(v))) return null;
  return Math.max(0, Math.min(100, Number(v)));
}
const hasOverride = (p) => p.cost_override != null && p.cost_override !== "";
export function plannedCostOf(p, config) {
  const planned = plannedPctOf(p);
  if (planned == null) return playerCostOf(p, config);
  const cur = effPctOf(p);
  if (hasOverride(p) && cur > 0) return Number(p.cost_override) * planned / cur;
  return coaOf(config, effResidencyOf(p, config)) * planned / 100;
}
// The player as they'd look next season with their planned aid applied.
export function withPlannedAid(p, config) {
  const planned = plannedPctOf(p);
  if (planned == null) return p;
  return { ...p, scholarship_pct: planned, on_scholarship: planned > 0, ...(hasOverride(p) ? { cost_override: plannedCostOf(p, config) } : {}) };
}
// Net $ effect of planned aid changes across the players who are staying.
export function aidPlanSummary(players, config) {
  let savings = 0, added = 0, reduceN = 0, increaseN = 0;
  (players || []).forEach((p) => {
    if (plannedPctOf(p) == null || !countsAidOf(p)) return;
    const d = plannedCostOf(p, config) - playerCostOf(p, config);
    if (d < 0) { savings -= d; reduceN += 1; } else if (d > 0) { added += d; increaseN += 1; }
  });
  return { savings, added, net: added - savings, reduceN, increaseN, count: reduceN + increaseN };
}

// Human label matching the per-player economics (single source of truth = effPctOf).
export function scholarshipPctLabel(player) {
  const p = effPctOf(player);
  if (!(p > 0)) return "Walk-on";
  if (p >= 100) return "Scholarship";
  return `${+Number(p).toFixed(2)}% aid`;
}

// Academic eligibility trickle-down. Source of truth = athlete.eligibility_status (set in Academics).
// Only "at_risk" (amber) and "ineligible" (red) surface a badge; eligible / medical_redshirt stay silent.
export const ELIG_FLAG_META = {
  at_risk: { short: "AT RISK", label: "Academically at risk", color: "#f97316" },
  ineligible: { short: "INELIGIBLE", label: "Academically ineligible", color: "#ef4444" },
};
export const eligFlag = (status) => ELIG_FLAG_META[status] || null;

// Medical statuses that make a player unavailable for competition/travel.
// Drives the Depth-Chart auto-park + Travel-Roster auto-exclude (source of truth = injuries).
export const MED_OUT_STATUSES = new Set(["out", "season_ending"]);
export const isMedicallyOut = (a) => !!a && MED_OUT_STATUSES.has(a.medical_status);

// Display a player's name as "Last, First" (falls back gracefully if one is missing).
export function nameLastFirst(a) {
  const ln = (a?.last_name || "").trim();
  const fn = (a?.first_name || "").trim();
  if (ln && fn) return `${ln}, ${fn}`;
  return ln || fn || "";
}

// ---- program snapshot (mirrors Roster Management cap math — single source of truth) ----
const NON_DEAD_STATUSES = ["active", "in_transition", "graduated", "incoming"];
export const isExitStatus = (p) => !!p.player_status && !NON_DEAD_STATUSES.includes(p.player_status);
export const isAidReleased = (p, todayStr) => p.aid_release_date && String(p.aid_release_date).slice(0, 10) <= todayStr;
export const countsAidOf = (p) => !(p.player_status === "incoming" && p.aid_counts === false);

// Should a player appear on roster-style printouts/exports? They must be on the active roster
// (active | incoming | in_transition — not archived / graduated / departed) AND not academically
// INELIGIBLE (they won't be active for us). Academically at-risk players stay on printouts.
export const isPrintableActive = (p) =>
  !!p && !p.archived && !isExitStatus(p) && p.player_status !== "graduated" && p.eligibility_status !== "ineligible";
export const printableRoster = (rows) => (rows || []).filter(isPrintableActive);
// On the current roster (not archived / graduated / cut / transferred / left) — the shared "is he still here?" gate.
export const isOnRoster = (p) => !!p && !p.archived && !isExitStatus(p) && p.player_status !== "graduated";

// Compute the current roster economics snapshot used by the Program Report.
export function computeRosterSnapshot(rows, config) {
  const today = new Date().toISOString().slice(0, 10);
  const active = (rows || []).filter((r) => !r.archived && !isExitStatus(r) && r.player_status !== "graduated");
  const deadRows = (rows || []).filter((r) => isExitStatus(r) && !isAidReleased(r, today));
  const deadMoney = deadRows.reduce((s, p) => s + playerCostOf(p, config), 0);
  const countable = active.filter((r) => (r.player_status || "active") === "active" && !r.dsa_exempt);
  const incoming = active.filter((r) => r.player_status === "incoming");
  const dsa = active.filter((r) => (r.player_status || "active") === "active" && r.dsa_exempt);

  let cost = 0, equiv = 0;
  active.forEach((p) => {
    if (!countsAidOf(p)) return;
    const c = playerCostOf(p, config);
    cost += c;
    const r = effResidencyOf(p, config);
    const denom = coaOf(config, r === "unknown" ? "out_state" : r);
    equiv += denom > 0 ? c / denom : effPctOf(p) / 100;
  });

  const targets = (config && config.position_targets) || {};
  const countAt = (pos) => active.filter((r) => (r.position || "").toUpperCase() === pos).length;
  const positionNeeds = Object.keys(targets)
    .map((pos) => { const P = pos.toUpperCase(); const target = Number(targets[pos] || 0); const have = countAt(P); return { pos: P, target, have, gap: target - have }; })
    .filter((x) => x.gap > 0)
    .sort((a, b) => b.gap - a.gap || compareFootballPos(a.pos, b.pos));

  return {
    activeRows: active, deadRows, deadMoney, countable, incoming, dsa,
    cost, equiv, positionNeeds, today,
    rosterLimit: Number(config?.roster_limit) || 105,
    equivLimit: Number(config?.equiv_limit) || 63,
    budget: Number(config?.scholarship_budget) || 0,
  };
}

// Shared next-season projection math (single source of truth for the Roster Forecast tab AND the
// Retention & Cut Board so the two screens never disagree). `retained` = players who stay (active
// roster minus assumed departures, with returns kept); `incomingCount` = committed/signed recruits;
// `avgAidPct` = expected average aid per signee (0-100).
export function computeForecastProjection(retained, incomingCount, config, avgAidPct = 100) {
  const retSnap = computeRosterSnapshot((retained || []).map((p) => withPlannedAid(p, config)), config);
  const aid = Math.max(0, Math.min(100, Number(avgAidPct) || 0)) / 100;
  const n = Number(incomingCount) || 0;
  const incEquiv = n * aid;
  const incCost = n * aid * coaOf(config, "out_state");
  return {
    head: retained.length + n,
    equiv: retSnap.equiv + incEquiv,
    cost: retSnap.cost + incCost,
    retainedEquiv: retSnap.equiv,
    retainedCost: retSnap.cost,
  };
}

// Does this player CURRENTLY count toward the House roster limit (105)?
// Single source of truth for the Roster-Limit Counter + 105 Projection board.
// Excludes non-active statuses, DSA-exempt, and designated/off-roster House statuses.
export function countsTowardLimit(a) {
  if (!a) return false;
  if ((a.player_status || "active") !== "active") return false;
  if (a.dsa_exempt) return false;
  if (a.house_roster_status === "designated_exception" || a.house_roster_status === "not_on_roster") return false;
  return true;
}

// Football side-of-ball classification for practice-number conflict rules.
const _OFFENSE = new Set(["QB", "RB", "FB", "HB", "TB", "SB", "TE", "WR", "SL", "OC", "C", "OG", "G", "OT", "T", "OL"]);
const _DEFENSE = new Set(["NT", "DT", "DE", "EDGE", "DL", "ILB", "OLB", "MLB", "LB", "MIKE", "WILL", "SAM", "CB", "DB", "NB", "NCB", "S", "FS", "SS", "SAF", "ROV"]);
const _SPECIAL = new Set(["P", "K", "PK", "LS", "H", "KR", "PR", "ST"]);
export function sideOfBall(position) {
  const p = (position || "").toUpperCase().trim();
  if (_OFFENSE.has(p)) return "offense";
  if (_DEFENSE.has(p)) return "defense";
  if (_SPECIAL.has(p)) return "special";
  return "other";
}

// IDs of active players sharing a practice number with another player ON THE SAME SIDE.
// Duplicate practice numbers across sides (e.g. offense + defense) are allowed.
export function practiceConflictIds(roster) {
  const buckets = {};
  (roster || []).forEach((a) => {
    if (a.archived) return;
    if ((a.player_status || "active") !== "active") return;
    const num = (a.practice_jersey_number || "").toString().trim();
    if (!num) return;
    const side = sideOfBall(a.position);
    if (side === "other") return;
    const key = `${side}|${num}`;
    (buckets[key] = buckets[key] || []).push(a.id);
  });
  const conflicts = new Set();
  Object.values(buckets).forEach((ids) => { if (ids.length > 1) ids.forEach((id) => conflicts.add(id)); });
  return conflicts;
}
